import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  Logout as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  Flight as FlightIcon,
  AdminPanelSettings as AdminIcon,
  Hotel as HotelFinIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import Profileicon from '../../assets/profileicon.png';
import LoginModal from '../Auth/LoginModal';
import SignupModal from '../Auth/SignupModal';

const UserAccountMenu = ({
  variant = 'desktop',
  showBrandChrome = false,
  /** Compact trigger styling: `brand` = maroon headers (white icon); `light` = light bars e.g. account page */
  compactSurface = 'brand',
  /** Compact trigger icon: `profile` (default) or `menu` (hamburger, e.g. account page) */
  compactIcon = 'profile',
}) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const userMenuOpen = Boolean(userMenuAnchor);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [signupPrefilledPhone, setSignupPrefilledPhone] = useState('');

  const handleUserMenuOpen = (e) => setUserMenuAnchor(e.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);
  const handleAccountClick = () => { handleUserMenuClose(); navigate('/account'); };
  const handleMyTripsClick = () => { handleUserMenuClose(); navigate('/my-trips'); };
  const handleHotelsAdminClick = () => { handleUserMenuClose(); navigate('/admin/hotels'); };
  const handleFlightsAdminClick = () => { handleUserMenuClose(); navigate('/admin/flights'); };
  const handleIssueMonitorClick = () => { handleUserMenuClose(); navigate('/admin/issues'); };
  const handleRefundManagementClick = () => { handleUserMenuClose(); navigate('/admin/refunds'); };
  const handleServiceRequestsClick = () => { handleUserMenuClose(); navigate('/admin/service-requests'); };
  const handleCampaignsClick = () => { handleUserMenuClose(); navigate('/admin/campaigns'); };
  const handleCampaignOffersClick = () => { handleUserMenuClose(); navigate('/admin/campaign-offers'); };
  const handleLogout = () => { handleUserMenuClose(); logout(); navigate('/'); };
  const isAdmin = !!user?.isAdmin;
  const menuAlignsLeft = variant === 'compact' && compactIcon === 'menu';

  const accountMenu = (
    <Menu
      anchorEl={userMenuAnchor}
      open={userMenuOpen}
      onClose={handleUserMenuClose}
      transformOrigin={{ horizontal: menuAlignsLeft ? 'left' : 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: menuAlignsLeft ? 'left' : 'right', vertical: 'bottom' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: '12px',
            boxShadow: 'var(--yovo-shadow-lg)',
          },
        },
      }}
    >
      <MenuItem onClick={handleAccountClick}>
        <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary="Account Details" />
      </MenuItem>
      <MenuItem onClick={handleMyTripsClick}>
        <ListItemIcon><FlightIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary="My Trips" />
      </MenuItem>
      {isAdmin && (
        <MenuItem onClick={handleHotelsAdminClick}>
          <ListItemIcon><HotelFinIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Hotels Admin" />
        </MenuItem>
      )}
      {isAdmin && (
        <MenuItem onClick={handleFlightsAdminClick}>
          <ListItemIcon><AdminIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Flights Admin" />
        </MenuItem>
      )}
      {isAdmin && (
        <MenuItem onClick={handleIssueMonitorClick}>
          <ListItemIcon><AdminIcon fontSize="small" sx={{ color: '#c62828' }} /></ListItemIcon>
          <ListItemText primary="Issue Monitor" />
        </MenuItem>
      )}
      {isAdmin && (
        <MenuItem onClick={handleRefundManagementClick}>
          <ListItemIcon><AdminIcon fontSize="small" sx={{ color: '#1565c0' }} /></ListItemIcon>
          <ListItemText primary="Refund Management" />
        </MenuItem>
      )}
      {isAdmin && (
        <MenuItem onClick={handleServiceRequestsClick}>
          <ListItemIcon><AdminIcon fontSize="small" sx={{ color: '#7b1fa2' }} /></ListItemIcon>
          <ListItemText primary="Service Requests" />
        </MenuItem>
      )}
      {isAdmin && (
        <MenuItem onClick={handleCampaignsClick}>
          <ListItemIcon><AdminIcon fontSize="small" sx={{ color: '#e65100' }} /></ListItemIcon>
          <ListItemText primary="Campaigns" />
        </MenuItem>
      )}
      {isAdmin && (
        <MenuItem onClick={handleCampaignOffersClick}>
          <ListItemIcon><AdminIcon fontSize="small" sx={{ color: '#2e7d32' }} /></ListItemIcon>
          <ListItemText primary="Campaign Offers" />
        </MenuItem>
      )}
      <Divider />
      <MenuItem onClick={handleLogout}>
        <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary="Log out" />
      </MenuItem>
    </Menu>
  );

  if (variant === 'compact') {
    if (!isAuthenticated) return null;

    const onLightSurface = compactSurface === 'light';
    const useMenuIcon = compactIcon === 'menu';

    return (
      <>
        <IconButton
          onClick={handleUserMenuOpen}
          aria-haspopup="true"
          aria-expanded={userMenuOpen ? 'true' : 'false'}
          aria-label={useMenuIcon ? 'Navigation menu' : 'Account menu'}
          sx={{
            color: onLightSurface ? '#20202A' : '#FBFBFB',
            p: 0.5,
            flexShrink: 0,
            minWidth: '2.75rem',
            minHeight: '2.75rem',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
            '&:active': {
              bgcolor: onLightSurface ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.15)',
            },
          }}
        >
          {useMenuIcon ? (
            <MenuIcon sx={{ fontSize: 24 }} />
          ) : (
            <img
              src={Profileicon}
              alt=""
              aria-hidden
              style={{
                width: 20,
                height: 20,
                filter: onLightSurface ? 'none' : 'brightness(0) invert(1)',
              }}
            />
          )}
        </IconButton>
        {accountMenu}
      </>
    );
  }

  if (isAuthenticated) {
    return (
      <>
        <Box
          className="yovo-header-profile-cluster yovo-header-profile-cluster--logged-in"
          onClick={handleUserMenuOpen}
          aria-haspopup="true"
          aria-expanded={userMenuOpen ? 'true' : 'false'}
          aria-label="Account menu"
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            minWidth: 0,
            '&:hover': { opacity: 0.88 },
          }}
        >
          <IconButton
            sx={{ color: '#666', p: '6px', flexShrink: 0, '&:hover': { background: 'transparent' } }}
            tabIndex={-1}
          >
            <img
              src={Profileicon}
              alt=""
              aria-hidden
              style={{
                width: 20,
                height: 20,
                filter: showBrandChrome ? 'brightness(0) invert(1)' : 'none',
                transition: 'filter 0.3s ease',
              }}
            />
          </IconButton>
          <Typography
            sx={{
              color: showBrandChrome ? '#FBFBFB' : 'var(--yovo-base-black)',
              fontFamily: 'var(--yovo-font-primary)',
              fontSize: 'var(--yovo-text-base)',
              fontWeight: 600,
              lineHeight: 'var(--yovo-leading-base)',
              letterSpacing: 'var(--yovo-tracking-wider)',
              ml: 0.25,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              transition: 'color 0.3s ease',
            }}
          >
            Hello {user?.first_name || user?.username || 'User'}
          </Typography>
          <ExpandMoreIcon
            sx={{
              color: showBrandChrome ? '#FBFBFB' : 'var(--yovo-base-black)',
              fontSize: 20,
              ml: 0.125,
              flexShrink: 0,
              transform: userMenuOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s ease, color 0.3s ease',
            }}
          />
        </Box>
        {accountMenu}
      </>
    );
  }

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setLoginModalOpen(true)}
        className="yovo-header-profile-cluster"
        sx={{
          background: showBrandChrome ? '#20202A' : 'var(--yovo-base-black)',
          color: '#FBFBFB',
          borderRadius: '16px',
          minWidth: 'auto',
          width: 'auto',
          height: { xs: '44px', sm: '44px', md: '48px' },
          px: { xs: '16px', sm: '18px', md: '24px' },
          py: { xs: '8px', md: '10px' },
          fontFamily: 'var(--yovo-font-primary)',
          fontSize: 'clamp(14px, 3.5vw, 16px)',
          fontWeight: 600,
          lineHeight: 1.25,
          letterSpacing: '0.4px',
          textTransform: 'none',
          flexShrink: 0,
          boxShadow: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          transition: 'background 0.3s ease',
          '&:hover': { background: '#333', boxShadow: 'none' },
        }}
      >
        <PersonIcon sx={{ fontSize: '18px', flexShrink: 0 }} />
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>Login</Box>
      </Button>
      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onSwitchToSignup={(verifiedPhone) => {
          setLoginModalOpen(false);
          setSignupPrefilledPhone(verifiedPhone || '');
          setSignupModalOpen(true);
        }}
      />
      <SignupModal
        open={signupModalOpen}
        onClose={() => { setSignupModalOpen(false); setSignupPrefilledPhone(''); }}
        onSwitchToLogin={() => { setSignupModalOpen(false); setSignupPrefilledPhone(''); setLoginModalOpen(true); }}
        prefilledPhone={signupPrefilledPhone}
      />
    </>
  );
};

export default UserAccountMenu;
